import type {
	ContractCostBreakDto,
	ContractPriceBreakDto,
} from '@supply-mover-2-0/shared';

export type ContractPriceBreak = Pick<
	ContractPriceBreakDto,
	'minimumQuantity' | 'price'
>;

/** Map of itemId → array of price breaks, as returned by getContractPricesForShopItems */
export type ContractPriceMap = Record<number, Array<ContractPriceBreak>>;

export type ContractCostBreak = Pick<
	ContractCostBreakDto,
	'minimumQuantity' | 'acquisitionCost' | 'sellCost'
>;

/** Map of itemId → array of cost breaks, as returned by getContractCostsForShopItems */
export type ContractCostMap = Record<number, Array<ContractCostBreak>>;

/**
 * Find the applicable contract price for an item at a given quantity.
 * Extracts the breaks for the item from the map, sorts ascending by
 * minimumQuantity, and returns the highest qualifying tier's price.
 */
export function getApplicableContractPrice(
	contractPrices: ContractPriceMap | null | undefined,
	itemId: number,
	quantity: number,
): number | null {
	const breaks = contractPrices?.[itemId];

	if (breaks == null || breaks.length === 0) {
		return null;
	}

	const sorted = [...breaks].sort(
		(a, b) => a.minimumQuantity - b.minimumQuantity,
	);
	let applicablePrice: number | null = null;

	for (const brk of sorted) {
		if (quantity >= brk.minimumQuantity) {
			applicablePrice = brk.price;
		}
	}

	return applicablePrice;
}

/**
 * Find the applicable contract cost break for an item at a given quantity.
 * Mirrors getApplicableContractPrice: sorts the item's breaks ascending by
 * minimumQuantity and returns the highest qualifying tier. Returns null when no
 * break qualifies (e.g. the lowest tier's minimumQuantity exceeds `quantity`),
 * which the caller treats as "inherit the item's own cost." Each break may carry
 * a null acquisitionCost or sellCost individually (that dimension is not
 * contract-overridden), so callers must null-check per dimension too.
 */
export function getApplicableContractCost(
	contractCosts: ContractCostMap | null | undefined,
	itemId: number,
	quantity: number,
): ContractCostBreak | null {
	const breaks = contractCosts?.[itemId];

	if (breaks == null || breaks.length === 0) {
		return null;
	}

	const sorted = [...breaks].sort(
		(a, b) => a.minimumQuantity - b.minimumQuantity,
	);
	let applicable: ContractCostBreak | null = null;

	for (const brk of sorted) {
		if (quantity >= brk.minimumQuantity) {
			applicable = brk;
		}
	}

	return applicable;
}

/**
 * Check if a price matches the applicable contract price for an item/quantity.
 * Uses tolerance-based comparison to handle floating point precision.
 */
export function isPriceFromContract(
	contractPrices: ContractPriceMap | null | undefined,
	itemId: number,
	price: number,
	quantity: number,
): boolean {
	const contractPrice = getApplicableContractPrice(
		contractPrices,
		itemId,
		quantity,
	);

	return contractPrice != null && Math.abs(price - contractPrice) < 0.001;
}

/**
 * Get the effective display price for an item.
 * Returns the contract price if one applies, otherwise the list price.
 */
export function getEffectiveItemPrice(
	contractPrices: ContractPriceMap | null | undefined,
	itemId: number,
	quantity: number,
	listPrice: number,
): number {
	return (
		getApplicableContractPrice(contractPrices, itemId, quantity) ?? listPrice
	);
}
